import type { QueryResultSet, QueryColumn } from '../../../../types/queryExecution';
import type { PopulationProgressEntry } from './usePopulationProgress';

// Stable empty references so placeholder result sets built during population
// don't change identity on every progress tick. Downstream memos in
// ResultsGrid (e.g. normalizedResults) use reference equality; churning these
// references triggers the scroll-reset effect on every row batch.
const EMPTY_ROWS: Record<string, unknown>[] = [];
const EMPTY_COLUMNS: QueryColumn[] = [];

/**
 * Builds effectiveResultSets from population progress, preserving result-set
 * index positions. When /rs/1 arrives before /rs/0, slot 0 gets a placeholder
 * so activeCacheUri and effectiveResultSets stay index-aligned.
 */
export function buildEffectiveResultSets(
  existingResultSets: QueryResultSet[] | undefined,
  populationProgress: Record<number, PopulationProgressEntry> | null,
): QueryResultSet[] | undefined {
  if (existingResultSets?.length) return existingResultSets;
  if (!populationProgress || Object.keys(populationProgress).length === 0) return existingResultSets;
  const entries = Object.values(populationProgress).filter((e) => e.columns.length > 0);
  if (entries.length === 0) return existingResultSets;
  const maxIndex = entries.reduce((max, e) => Math.max(max, e.resultSetIndex), 0);
  const sparse: QueryResultSet[] = [];
  for (let i = 0; i <= maxIndex; i++) {
    const entry = populationProgress[i];
    if (entry && entry.columns.length > 0) {
      // For inline mode, surface accumulated rows so the grid can render partial data
      // without going through a backend source. For windowed mode, leave rows empty —
      // the grid fetches via result-cache:get-window.
      const rows = entry.mode === 'inline' ? entry.rows : EMPTY_ROWS;
      sparse.push({ columns: entry.columns, rows, rowCount: entry.loadedRowCount });
    } else {
      sparse.push({ columns: EMPTY_COLUMNS, rows: EMPTY_ROWS, rowCount: 0 });
    }
  }
  return sparse;
}
